import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Database, Plus, Trash2, RefreshCw, Users, BookOpen, MapPin, 
  Building2, Award, Gift, Loader2, AlertTriangle, Check, Globe
} from 'lucide-react';

export default function AdminSeedDataPage({ embedded = false }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [status, setStatus] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/seed/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch seed status:', error);
      toast.error('Failed to load seed data status');
    } finally {
      setLoading(false);
    }
  };

  const performAction = async (action) => {
    setConfirmDialog({ open: false, action: null });
    setActionLoading(action);
    
    try {
      const response = await api.post(`/admin/seed/${action}`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchStatus();
      } else {
        toast.error(response.data.message || `Failed to ${action} seed data`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} seed data`);
    } finally {
      setActionLoading(null);
    }
  };

  const confirmAction = (action) => {
    setConfirmDialog({ open: true, action });
  };

  const getActionDescription = (action) => {
    switch (action) {
      case 'add':
        return 'This will add sample data to your database including users, courses, learners, and more. Existing data will not be affected.';
      case 'delete':
        return 'This will DELETE all seed data from your database (courses, learners, enrollments, etc.). The admin user will be preserved so you can still log in and re-add data.';
      case 'reset':
        return 'This will DELETE all seed data and then ADD fresh seed data. This is useful for a clean testing environment.';
      case 'purge-demo':
        return 'This will permanently DELETE all records marked as demo/seed data (courses, blog posts, users, learners, etc.). Only real data you imported will be preserved. Your admin account will NOT be deleted.';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-seed-data-page">
      {!embedded && (
        <div>
          <h1 className="font-heading text-2xl font-bold">Seed Data Management</h1>
          <p className="text-muted-foreground">Add or remove sample data for testing and development</p>
        </div>
      )}

      {/* Action Buttons */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Seed Data Actions
          </CardTitle>
          <CardDescription>
            Manage sample data in your database for testing purposes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Button 
              onClick={() => confirmAction('add')}
              disabled={actionLoading !== null}
              className="flex items-center gap-2"
              data-testid="seed-add-btn"
            >
              {actionLoading === 'add' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Add Seed Data
            </Button>
            
            <Button 
              variant="destructive"
              onClick={() => confirmAction('delete')}
              disabled={actionLoading !== null}
              className="flex items-center gap-2"
              data-testid="seed-delete-btn"
            >
              {actionLoading === 'delete' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete Seed Data
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => confirmAction('reset')}
              disabled={actionLoading !== null}
              className="flex items-center gap-2"
              data-testid="seed-reset-btn"
            >
              {actionLoading === 'reset' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Reset Seed Data
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => confirmAction('purge-demo')}
              disabled={actionLoading !== null}
              className="flex items-center gap-2 border-amber-400 text-amber-700 hover:bg-amber-50"
              data-testid="seed-purge-demo-btn"
            >
              {actionLoading === 'purge-demo' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              Purge All Demo Data
            </Button>
            
            <Button 
              variant="ghost"
              onClick={fetchStatus}
              disabled={actionLoading !== null}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh Status
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Current Data Status */}
      {status && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{status.users?.total || 0}</p>
                  <p className="text-xs text-muted-foreground">Users</p>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                <Badge variant="outline" className="text-xs">
                  {status.users?.admins || 0} admins
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {status.users?.instructors || 0} instructors
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <BookOpen className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{status.courses || 0}</p>
                  <p className="text-xs text-muted-foreground">Courses</p>
                </div>
              </div>
              <div className="mt-2">
                <Badge variant="outline" className="text-xs">
                  {status.sections || 0} sections
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <MapPin className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{status.locations || 0}</p>
                  <p className="text-xs text-muted-foreground">Locations</p>
                </div>
              </div>
              <div className="mt-2">
                <Badge variant="outline" className="text-xs">
                  {status.kits || 0} kits
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Globe className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{status.franchises || 0}</p>
                  <p className="text-xs text-muted-foreground">Franchises</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100">
                  <Building2 className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {(status.partners?.school_districts || 0) + 
                     (status.partners?.partner_schools || 0) +
                     (status.partners?.park_districts || 0) +
                     (status.partners?.libraries || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Partners</p>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                <Badge variant="outline" className="text-xs">
                  {status.partners?.school_districts || 0} districts
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {status.partners?.partner_schools || 0} schools
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-100">
                  <Users className="h-5 w-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{status.learners || 0}</p>
                  <p className="text-xs text-muted-foreground">Learners</p>
                </div>
              </div>
              <div className="mt-2">
                <Badge variant="outline" className="text-xs">
                  {status.enrollments || 0} enrollments
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-100">
                  <Award className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{status.badges || 0}</p>
                  <p className="text-xs text-muted-foreground">Badges</p>
                </div>
              </div>
              <div className="mt-2">
                <Badge variant="outline" className="text-xs">
                  {status.badge_issuances || 0} issued
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-pink-100">
                  <Gift className="h-5 w-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{status.rewards || 0}</p>
                  <p className="text-xs text-muted-foreground">Rewards</p>
                </div>
              </div>
              <div className="mt-2">
                <Badge variant="outline" className="text-xs">
                  {status.raffles || 0} raffles
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Test Credentials Info */}
      <Card>
        <CardHeader>
          <CardTitle>Test Credentials</CardTitle>
          <CardDescription>Use these credentials to test different user roles</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-muted">
              <p className="font-medium text-sm mb-2">Admin</p>
              <p className="text-xs text-muted-foreground">admin@readysetstem.org</p>
              <p className="text-xs text-muted-foreground">Admin123!</p>
            </div>
            <div className="p-4 rounded-lg bg-muted">
              <p className="font-medium text-sm mb-2">Instructor</p>
              <p className="text-xs text-muted-foreground">sarah.instructor@readysetstem.org</p>
              <p className="text-xs text-muted-foreground">Instructor123!</p>
            </div>
            <div className="p-4 rounded-lg bg-muted">
              <p className="font-medium text-sm mb-2">Parent</p>
              <p className="text-xs text-muted-foreground">parent@example.com</p>
              <p className="text-xs text-muted-foreground">Parent123!</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmDialog.action === 'delete' && <AlertTriangle className="h-5 w-5 text-destructive" />}
              Confirm {confirmDialog.action === 'add' ? 'Add' : confirmDialog.action === 'delete' ? 'Delete' : 'Reset'} Seed Data
            </DialogTitle>
            <DialogDescription>
              {getActionDescription(confirmDialog.action)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, action: null })}>
              Cancel
            </Button>
            <Button 
              variant={confirmDialog.action === 'delete' ? 'destructive' : 'default'}
              onClick={() => performAction(confirmDialog.action)}
              data-testid="confirm-seed-action-btn"
            >
              <Check className="h-4 w-4 mr-2" />
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
